import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import axiosInstance from '../../api/axiosInstance';
import { useProject } from '../../context/ProjectContext';
import useWorkspaceRouting from '../../hooks/useWorkspaceRouting';
import { buildWorkspaceDashboardPath } from '../../utils/workspaceRouteUtils';
import { formatDateTime } from '../../utils/dateTimeFormat';
import {
  Breadcrumbs,
  DataTable,
  ErrorState,
  LoadingState,
  MetricCard,
  PageScaffold,
  StatusBadge,
} from '../ui';

const asArray = (...candidates) => {
  for (const c of candidates) {
    if (Array.isArray(c)) return c;
  }
  return [];
};

const toCount = (...values) => {
  for (const v of values) {
    if (v != null && v !== '' && Number.isFinite(Number(v))) return Number(v);
  }
  return null;
};

const personName = (value) => (typeof value === 'string' ? value : value?.name || null);

const normalizeStatus = (row) =>
  String(row?.status || row?.execution_status || '').toLowerCase();

const isFailedRow = (row) => {
  const status = normalizeStatus(row);
  if (['failed', 'fail', 'error'].includes(status)) return true;
  return toCount(row?.failed, row?.total_failed) > 0;
};

const formatDuration = (seconds) => {
  const n = Number(seconds);
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n < 60) return `${n.toFixed(n < 10 ? 2 : 1)}s`;
  return `${Math.floor(n / 60)}m ${Math.round(n % 60)}s`;
};

const childRowKey = (type, row, index) => {
  if (type === 'plan') {
    return String(row.suite_execution_id || row.suite_id || row.id || index);
  }
  return String(row.test_case_id || row.execution_id || row.id || index);
};

/**
 * Execution detail — plan / suite / case.
 * Suite breakdown must read `suite_executions` (same as ExecutionHistoryModal).
 */
const ExecutionDetailPage = () => {
  const { executionId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { selectedProject } = useProject();
  const { getWorkspaceIdFromURL, getProjectIdFromURL } = useWorkspaceRouting();

  const type = searchParams.get('type') || 'plan';
  const entityId = searchParams.get('entity_id');
  const name = searchParams.get('name') || 'Execution';
  const projectId = getProjectIdFromURL() || selectedProject?.id;

  const [loading, setLoading] = useState(true);
  const [childrenLoading, setChildrenLoading] = useState(false);
  const [error, setError] = useState(null);
  const [childrenError, setChildrenError] = useState(null);
  const [execution, setExecution] = useState(null);
  const [children, setChildren] = useState([]);
  const [expandedSuiteId, setExpandedSuiteId] = useState(null);
  const [suiteCases, setSuiteCases] = useState([]);
  const [suiteCasesLoading, setSuiteCasesLoading] = useState(false);

  const goBack = useCallback(() => {
    const workspaceId = getWorkspaceIdFromURL();
    if (workspaceId) {
      navigate(buildWorkspaceDashboardPath(workspaceId, projectId, 'executions'));
    } else {
      navigate('/dashboard/executions');
    }
  }, [navigate, getWorkspaceIdFromURL, projectId]);

  const loadSuiteCases = useCallback(async (suiteExecutionId) => {
    if (!suiteExecutionId) return;
    setSuiteCasesLoading(true);
    setSuiteCases([]);
    try {
      const res = await axiosInstance.get('/test_case/get_test_suite_case_execution', {
        params: { suite_execution_id: suiteExecutionId },
      });
      setSuiteCases(asArray(res?.test_cases, res?.data, res));
    } catch (err) {
      console.error(err);
      setSuiteCases([]);
    } finally {
      setSuiteCasesLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setChildrenError(null);
    setChildren([]);
    setExpandedSuiteId(null);
    setSuiteCases([]);
    try {
      if (type === 'plan' && entityId) {
        const hist = await axiosInstance.get('/test_case/get_test_plan_execution', {
          params: { test_plan_id: entityId },
        });
        const list = asArray(hist?.data, hist?.executions, hist);
        const match =
          list.find(
            (r) =>
              String(r.test_plan_execution_id || r.execution_id || r.id) ===
              String(executionId)
          ) || null;
        setExecution(match);

        const planExecId = match?.test_plan_execution_id || executionId;
        if (planExecId) {
          setChildrenLoading(true);
          try {
            const suiteRes = await axiosInstance.get(
              '/test_case/get_test_plan_suite_execution',
              { params: { test_plan_execution_id: planExecId } }
            );
            // API shape used by ExecutionHistoryModal: { suite_executions: [...] }
            setChildren(
              asArray(
                suiteRes?.suite_executions,
                suiteRes?.data?.suite_executions,
                suiteRes?.suites,
                suiteRes?.data,
                suiteRes
              )
            );
          } catch (err) {
            console.error(err);
            setChildren([]);
            setChildrenError(err?.message || 'Failed to load suite breakdown');
          } finally {
            setChildrenLoading(false);
          }
        }
      } else if (type === 'suite' && entityId) {
        const hist = await axiosInstance.get('/test_case/get_test_suite_execution', {
          params: { suite_id: entityId },
        });
        const list = asArray(hist?.data, hist?.executions, hist);
        const match =
          list.find(
            (r) =>
              String(r.suite_execution_id || r.execution_id || r.id) ===
              String(executionId)
          ) || null;
        setExecution(match);

        const suiteExecId = match?.suite_execution_id || executionId;
        if (suiteExecId) {
          setChildrenLoading(true);
          try {
            const caseRes = await axiosInstance.get(
              '/test_case/get_test_suite_case_execution',
              { params: { suite_execution_id: suiteExecId } }
            );
            setChildren(asArray(caseRes?.test_cases, caseRes?.data, caseRes));
          } catch (err) {
            console.error(err);
            setChildren([]);
            setChildrenError(err?.message || 'Failed to load case breakdown');
          } finally {
            setChildrenLoading(false);
          }
        }
      } else if (type === 'case' && entityId) {
        const hist = await axiosInstance.get('/execute_file/execution_history_list', {
          params: { test_case_id: entityId },
        });
        const list = asArray(hist?.data, hist);
        const match =
          list.find((r) => String(r.execution_id || r.id) === String(executionId)) ||
          null;
        setExecution(match);
        setChildren([]);
      } else {
        setError('Missing execution type or entity context.');
      }
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Failed to load execution');
    } finally {
      setLoading(false);
    }
  }, [type, entityId, executionId]);

  useEffect(() => {
    load();
  }, [load]);

  const metrics = useMemo(() => {
    const passed = toCount(execution?.passed, execution?.total_passed);
    const failed = toCount(execution?.failed, execution?.total_failed);
    const skipped = toCount(execution?.skipped, execution?.total_skipped);
    const status = normalizeStatus(execution);
    return {
      passed: passed ?? (status === 'passed' || status === 'success' ? 1 : 0),
      failed: failed ?? (['failed', 'fail', 'error'].includes(status) ? 1 : 0),
      skipped: skipped ?? 0,
      total: toCount(execution?.total_test_cases, execution?.total, execution?.total_cases),
      passRate: toCount(execution?.pass_rate),
    };
  }, [execution]);

  const failures = useMemo(
    () => (Array.isArray(children) ? children : []).filter(isFailedRow),
    [children]
  );

  const childColumns =
    type === 'plan'
      ? [
          {
            key: 'name',
            header: 'Suite',
            render: (r) => (
              <button
                type="button"
                className="rt2-link"
                onClick={(e) => {
                  e.stopPropagation();
                  const id = r.suite_execution_id;
                  if (!id) return;
                  if (String(expandedSuiteId) === String(id)) {
                    setExpandedSuiteId(null);
                    setSuiteCases([]);
                  } else {
                    setExpandedSuiteId(id);
                    loadSuiteCases(id);
                  }
                }}
              >
                {r.suite_name || r.name || `Suite #${r.suite_id || r.suite_execution_id}`}
              </button>
            ),
          },
          {
            key: 'status',
            header: 'Status',
            render: (r) => <StatusBadge status={r.status || r.execution_status} />,
          },
          {
            key: 'results',
            header: 'Results',
            render: (r) => {
              const p = toCount(r.passed, r.total_passed) ?? 0;
              const f = toCount(r.failed, r.total_failed) ?? 0;
              const t = toCount(r.total_test_cases, r.total, r.total_cases);
              return (
                <span>
                  <span style={{ color: '#16a34a', fontWeight: 650 }}>{p}</span>
                  {' / '}
                  <span style={{ color: f > 0 ? '#dc2626' : '#94a3b8', fontWeight: 650 }}>{f}</span>
                  {t != null ? (
                    <span className="rt2-muted" style={{ marginLeft: 6 }}>
                      of {t}
                    </span>
                  ) : null}
                </span>
              );
            },
          },
          {
            key: 'pass_rate',
            header: 'Pass rate',
            render: (r) => {
              const rate = toCount(r.pass_rate);
              return rate != null ? `${rate}%` : '—';
            },
          },
          {
            key: 'duration',
            header: 'Duration',
            render: (r) => formatDuration(r.duration ?? r.execution_time_sec),
          },
        ]
      : [
          {
            key: 'name',
            header: 'Test Case',
            render: (r) => r.test_case_name || r.name || `#${r.test_case_id || r.id}`,
          },
          {
            key: 'status',
            header: 'Status',
            render: (r) => <StatusBadge status={r.status || r.execution_status} />,
          },
          {
            key: 'duration',
            header: 'Duration',
            render: (r) => formatDuration(r.duration ?? r.execution_time_sec),
          },
        ];

  const executionStatus = execution?.status || execution?.execution_status;
  const triggeredBy =
    personName(execution?.executed_by_name) ||
    personName(execution?.triggered_by) ||
    personName(execution?.created_by) ||
    '—';
  const environment =
    execution?.environment ||
    execution?.environment_name ||
    execution?.env ||
    '—';
  const startedAt = execution?.started_at || execution?.created_at || execution?.start_time;

  return (
    <PageScaffold
      eyebrow={`Run · ${type} execution`}
      title={name}
      description={`Execution #${executionId}${startedAt ? ` · ${formatDateTime(startedAt)}` : ''}`}
      compact
      breadcrumbs={
        <Breadcrumbs
          items={[
            { label: 'Executions', path: 'executions' },
            { label: `#${executionId}` },
          ]}
          onNavigate={goBack}
        />
      }
      actions={
        <>
          {executionStatus ? <StatusBadge status={executionStatus} /> : null}
          <button type="button" className="rt-btn rt-btn--secondary" onClick={goBack}>
            ← Executions
          </button>
          {failures.length > 0 && (
            <button
              type="button"
              className="rt-btn rt-btn--primary"
              onClick={() => {
                const workspaceId = getWorkspaceIdFromURL();
                if (workspaceId) {
                  navigate(
                    buildWorkspaceDashboardPath(workspaceId, projectId, 'intelligence?tab=learning')
                  );
                }
              }}
            >
              Analyze with AI
            </button>
          )}
        </>
      }
    >
      {loading && <LoadingState label="Loading execution…" />}
      {!loading && error && <ErrorState description={error} onRetry={load} />}
      {!loading && !error && !execution && (
        <ErrorState
          title="Execution not found"
          description={`Could not find execution #${executionId} for this ${type}.`}
          onRetry={load}
        />
      )}
      {!loading && !error && execution && (
        <>
          <div className="rt-metric-grid">
            <MetricCard label="Passed" tone="success" value={metrics.passed} />
            <MetricCard label="Failed" tone="danger" value={metrics.failed} />
            <MetricCard label="Skipped" tone="warning" value={metrics.skipped} />
            <MetricCard label="Environment" tone="info" value={environment} />
            <MetricCard
              label="Duration"
              tone="accent"
              value={formatDuration(
                execution.duration ?? execution.execution_time_sec ?? execution.duration_sec
              )}
            />
            <MetricCard label="Triggered by" tone="violet" value={triggeredBy} />
          </div>

          {type !== 'case' && (
            <div className="rt2-panel" style={{ marginTop: 14 }}>
              <div className="rt2-panel__header">
                <h2 className="rt2-panel__title">
                  {type === 'plan' ? 'Suites' : 'Cases'}
                </h2>
                {children.length > 0 ? (
                  <span className="rt2-muted">{children.length} items</span>
                ) : null}
              </div>
              <div className="rt2-panel__body" style={{ padding: 0 }}>
                {childrenLoading ? (
                  <div style={{ padding: 24 }}>
                    <LoadingState label="Loading breakdown…" />
                  </div>
                ) : childrenError ? (
                  <div style={{ padding: 16 }}>
                    <ErrorState description={childrenError} onRetry={load} />
                  </div>
                ) : (
                  <DataTable
                    columns={childColumns}
                    rows={children}
                    rowKey={type === 'plan' ? 'suite_execution_id' : 'test_case_id'}
                    emptyTitle="No breakdown available"
                    emptyDescription={
                      metrics.failed > 0 || metrics.passed > 0
                        ? 'Summary counts are available, but suite/case rows were not returned for this run.'
                        : 'This execution has no nested results yet.'
                    }
                  />
                )}
              </div>

              {type === 'plan' && expandedSuiteId ? (
                <div className="rt2-panel__body" style={{ borderTop: '1px solid #e2e8f0' }}>
                  <h3 className="rt2-panel__title" style={{ fontSize: '0.95rem', marginBottom: 10 }}>
                    Cases in suite execution #{expandedSuiteId}
                  </h3>
                  {suiteCasesLoading ? (
                    <LoadingState label="Loading cases…" />
                  ) : (
                    <DataTable
                      columns={[
                        {
                          key: 'name',
                          header: 'Test Case',
                          render: (r) =>
                            r.test_case_name || r.name || `#${r.test_case_id || r.id}`,
                        },
                        {
                          key: 'status',
                          header: 'Status',
                          render: (r) => (
                            <StatusBadge status={r.status || r.execution_status} />
                          ),
                        },
                        {
                          key: 'duration',
                          header: 'Duration',
                          render: (r) => formatDuration(r.duration ?? r.execution_time_sec),
                        },
                      ]}
                      rows={suiteCases}
                      rowKey="test_case_id"
                      emptyTitle="No cases for this suite run"
                    />
                  )}
                </div>
              ) : null}
            </div>
          )}

          <div className="rt2-panel" style={{ marginTop: 14 }}>
            <div className="rt2-panel__header">
              <h2 className="rt2-panel__title">Failures</h2>
              {failures.length > 0 ? (
                <span className="rt2-muted">{failures.length}</span>
              ) : null}
            </div>
            <div className="rt2-panel__body">
              {childrenLoading ? (
                <LoadingState label="Checking failures…" />
              ) : failures.length === 0 ? (
                <p className="rt2-muted" style={{ margin: 0 }}>
                  {metrics.failed > 0
                    ? `Summary reports ${metrics.failed} failed, but no failed suites/cases were returned in the breakdown.`
                    : 'No failures recorded for this execution.'}
                </p>
              ) : (
                failures.map((f, index) => (
                  <div
                    className="rt2-attention-item"
                    key={childRowKey(type, f, index)}
                  >
                    <span>
                      {f.suite_name ||
                        f.test_case_name ||
                        f.name ||
                        'Failed item'}
                      {toCount(f.failed, f.total_failed) > 0 ? (
                        <span className="rt2-muted" style={{ marginLeft: 8 }}>
                          {toCount(f.failed, f.total_failed)} failed
                        </span>
                      ) : null}
                    </span>
                    <StatusBadge status={f.status || f.execution_status || 'failed'} />
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </PageScaffold>
  );
};

export default ExecutionDetailPage;
